import { urlNamesRouteDeparture } from '../state/urlNamesRouteDeparture'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { BrowserGeolocationGateway, createLocationStore } from '@metro/location'
import { isUserLocationDotVisible } from './userLocationDot'
import { GeolocationContext } from '../state/geolocationContext'
import { MapFocusContext, createMapFocusHandle } from '../state/mapFocusContext'
import { SchemaNearestStationProvider } from '../state/schemaNearestStationProvider'
import { haversineMeters } from '../state/nearestStationProvider'
import { isInsideHomeRegion } from '../state/homeRegion'
import {
  createLocationConsumerService,
  type LocationConsumerService,
  type LocationConsumerState,
} from '../state/locationConsumerService'
import { handleMyLocationRequested } from '../state/geolocationController'
import { runLaunchGeolocation } from '../state/geolocationLaunch'
import { resolveStationBySlug } from '../interaction/stationResolver'
import {
  reportAuthorizationChanged,
  reportAuthorizationRequested,
  reportInitialUserLocationRequested,
  reportLatestLocationRequested,
  reportLocationRequested,
  reportLocationUpdated,
  reportUserLocationRequested,
} from '../analytics/locationEvents'
import { reportMyLocationTapped } from '../analytics/mapSurfaceEvents'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { readInitialViewOverride } from './MetroMapCanvas.helpers'
import { useSchema } from './useSchema'
import { useMetroMapStore, useMetroMapDispatch } from '../state/useMetroMapStore'
import { LocationDeniedModal } from './LocationDeniedModal'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'

/**
 * Owns the TASK-391 geolocation wiring for the whole app:
 *
 * - builds the `Location` store (browser gateway + schema-backed
 *   nearest-station provider),
 * - exposes `onMyLocation` to the button host surfaces via
 *   {@link GeolocationContext},
 * - publishes the imperative camera-focus seam ({@link MapFocusContext})
 *   that MetroMapCanvas fills in,
 * - tracks the denied-modal open state.
 *
 * Mounted in `AppShell` inside `<MetroMapStoreProvider>` (needs the map
 * store) and below `<SchemaProvider>` (needs the offline station DB).
 */
export function GeolocationController({ children }: { children: ReactNode }): ReactElement {
  const schema = useSchema()
  const mapStore = useMetroMapStore()
  const dispatchMap = useMetroMapDispatch()
  const [deniedModalOpen, setDeniedModalOpen] = useState(false)

  // Shared focus handle — MetroMapCanvas registers its `focusOn` here,
  // the geolocation flows invoke it. Created once per controller.
  const mapFocusHandle = useMemo(() => createMapFocusHandle(), [])

  // The browser gateway is stable for the app lifetime; the controller
  // reads `permissionStatus()` off it directly for the launch-time
  // probe (which must NOT prompt).
  const gateway = useMemo(() => new BrowserGeolocationGateway(), [])

  // Built once schema is available. The `Location` store itself is
  // pure, product-agnostic location; the metro location-consumer service
  // owns the home-region / airport remap + nearest-station resolution (§2b).
  const locationStore = useMemo(() => {
    if (schema === null) return null
    return createLocationStore({
      gateway,
      onShowDeniedModal: () => setDeniedModalOpen(true),
    })
  }, [schema, gateway])

  const consumer = useMemo<LocationConsumerService | null>(() => {
    if (schema === null || locationStore === null) return null
    return createLocationConsumerService({
      store: locationStore,
      nearestProvider: new SchemaNearestStationProvider(schema.stations),
    })
  }, [schema, locationStore])

  useEffect(() => {
    return () => consumer?.dispose()
  }, [consumer])

  // Resolves the consumer-service nearest station into a map snapshot.
  // The nearest station id is a per-line cluster record id (e.g.
  // `circle.940GZZLUKSX`) present directly in `schema.stations`.
  const resolveNearestSnapshot = useCallback(async (): Promise<SelectedStationSnapshot | null> => {
    if (consumer === null || schema === null) return null
    // Mirror the iOS Location fix sequence: `.requestLocation` (a fix is
    // requested) → `.getLatestLocation` (read the cached fix).
    // `requestNearest()` is the web equivalent: it dispatches `RequestFix`
    // then re-resolves the domain state. The respond step emits nothing:
    // `response_location` was removed 2026-07-28 on all three platforms (one
    // emit per fix made it 41 % of total event volume, no product meaning).
    reportLocationRequested()
    // G-7: these lifecycle events no longer carry the cached coordinate
    // (no raw lat/long leaves the device). They still fire — the "a cached
    // location was read / responded" signal is the non-PII useful part.
    reportLatestLocationRequested()
    const resolved = await consumer.requestNearest()
    const nearest = resolved.nearestStations[0]
    if (nearest === undefined) return null
    const record = schema.stations.find((st) => st.id === nearest.id)
    if (record === undefined) return null
    return {
      id: record.id,
      canvasX: record.x,
      canvasY: record.y,
      lineId: record.line.id,
      localName: record.localName,
      naptanId: record.naptanId,
    }
  }, [consumer, schema])

  // TASK-391 §3.3 — spinner state on the my-location button. Set the
  // moment the user taps; cleared when the fix resolves / fails, with a
  // 12 s safety net so the button never spins forever (e.g. permission
  // granted but the device never returns a signal). Mirrors the iOS
  // `isResolvingMyLocation` + `_scheduleMyLocationResolveTimeout`.
  const [isResolvingMyLocation, setIsResolvingMyLocation] = useState(false)
  const resolveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearResolveTimeout = useCallback((): void => {
    if (resolveTimeoutRef.current !== null) {
      clearTimeout(resolveTimeoutRef.current)
      resolveTimeoutRef.current = null
    }
  }, [])
  useEffect(() => clearResolveTimeout, [clearResolveTimeout])

  const onMyLocation = useCallback((): void => {
    if (locationStore === null) return
    // Re-tap guard — the button is also disabled while loading, but the
    // context handle can be invoked from anywhere, so guard here too.
    if (isResolvingMyLocation) return
    // `location_user_location_requested` — the explicit "find me" action
    // (mirrors iOS `.requestUserLocation`). Fired before the guard's work
    // so every genuine tap is counted.
    reportUserLocationRequested()
    // G-1 `my_location_tapped` — the cross-platform map-surface product
    // event (distinct from the iOS-parity location diagnostic above). One
    // emit here covers BOTH host surfaces (the phone in-card button and the
    // tablet/desktop floating button), since both invoke this context handle.
    reportMyLocationTapped(realMetroAnalyticsReporter)
    setIsResolvingMyLocation(true)
    clearResolveTimeout()
    resolveTimeoutRef.current = setTimeout(() => {
      resolveTimeoutRef.current = null
      setIsResolvingMyLocation(false)
    }, 12_000)
    const finishResolving = (): void => {
      clearResolveTimeout()
      setIsResolvingMyLocation(false)
    }
    void handleMyLocationRequested({
      getPermission: () => locationStore.getState().permissionStatus,
      getFromIsEmpty: () => mapStore.getState().routeSelection.fromStation === null,
      getToIsEmpty: () => mapStore.getState().routeSelection.toStation === null,
      // §3.3 first-time-user path: the browser permission prompt only
      // appears when `getCurrentPosition` runs (inside `requestFix`).
      // Querying status would return `notDetermined` and show nothing.
      // We drive a real fix: a resolved fix proves `granted`; a
      // `PERMISSION_DENIED` (code 1) rejection proves `denied`; any
      // other failure (timeout / position-unavailable) is left as
      // `notDetermined` so the controller no-ops instead of falsely
      // showing the denied modal.
      requestPermissionViaFix: async () => {
        // First read the real OS status — if the browser already has a
        // decision (`granted` / `denied`) there is no `prompt` state to
        // resolve and a fix would either be redundant or silently fail.
        const known = await gateway.permissionStatus()
        if (known === 'granted' || known === 'denied' || known === 'restricted') {
          return known
        }
        // `notDetermined` → the next `RequestFix` triggers the browser
        // permission prompt: `location_authorization_requested` (mirrors
        // iOS `.requestAuthorization`).
        reportAuthorizationRequested()
        try {
          await locationStore.dispatch({ kind: 'RequestFix' })
          // The fix resolved — the browser delivered a position, which
          // it only does once the user grants permission. The permission
          // transitioned not_determined → granted.
          reportAuthorizationChanged('granted')
          return 'granted'
        } catch (error) {
          const code = (error as GeolocationPositionError | undefined)?.code
          const resolved = code === 1 /* PERMISSION_DENIED */ ? 'denied' : 'notDetermined'
          // Only a real decision is a change worth reporting; a
          // timeout / position-unavailable leaves status not_determined
          // (no user decision), so skip those.
          if (resolved === 'denied') reportAuthorizationChanged('denied')
          return resolved
        }
      },
      resolveNearestSnapshot,
      focusOn: (target) => mapFocusHandle.focus(target),
      dispatchMap,
      showDeniedModal: () => setDeniedModalOpen(true),
    }).finally(finishResolving)
  }, [
    locationStore,
    mapStore,
    gateway,
    dispatchMap,
    resolveNearestSnapshot,
    mapFocusHandle,
    isResolvingMyLocation,
    clearResolveTimeout,
  ])

  // TASK-391 §3.6: live user-location dot target. Subscribes to the
  // metro location-consumer service, recomputes the nearest-station
  // canvas position + the show/hide rule whenever its state changes.
  const [dotTarget, setDotTarget] = useState<{ canvasX: number; canvasY: number } | null>(null)
  // `location_updated` fires once per NEW fix. The consumer only swaps
  // `userPoint` identity when the raw coordinate actually changes (it
  // dedupes on the underlying reference), so tracking the last-emitted
  // point reference here gives one emit per distinct fix — covering both
  // the button path and the live `watch` stream.
  const lastEmittedFixRef = useRef<unknown>(null)
  useEffect(() => {
    if (consumer === null || schema === null) return
    const recompute = (): void => {
      const state: LocationConsumerState = consumer.getState()
      // Emit `location_updated` on a fresh fix (byte-identical params with
      // iOS `.didUpdateLocations`). `userPoint` carries the browser
      // `accuracy` / `timestamp` metadata through the location store.
      if (state.userPoint !== null && state.userPoint !== lastEmittedFixRef.current) {
        lastEmittedFixRef.current = state.userPoint
        reportLocationUpdated(state.userPoint)
      }
      const nearest = state.nearestStations[0]
      if (state.userPoint === null || state.appPoint === null || nearest === undefined) {
        setDotTarget(null)
        return
      }
      // Dot-fix B (canon f5a9b97be): gate visibility on the REMAPPED
      // appPoint, not the raw userPoint. An out-of-region user's appPoint is
      // the city's airport (inside the region), so the dot stays VISIBLE —
      // snapped to the airport-nearest station — instead of vanishing abroad. iOS
      // feeds the remapped appLocation into UserLocationDotModel for the
      // same effect; we mirror the BEHAVIOR (visible abroad).
      const visible = isUserLocationDotVisible({
        insideUK: isInsideHomeRegion(state.appPoint),
        distanceMeters: haversineMeters(state.appPoint, nearest.point),
      })
      if (!visible) {
        setDotTarget(null)
        return
      }
      const record = schema.stations.find((st) => st.id === nearest.id)
      setDotTarget(record === undefined ? null : { canvasX: record.x, canvasY: record.y })
    }
    const unsubscribe = consumer.subscribe(recompute)
    recompute()
    return unsubscribe
  }, [consumer, schema])

  // §3.6: while permission is granted, run a live location stream so the
  // dot re-snaps as the user moves. Started once after the launch probe.
  useEffect(() => {
    if (locationStore === null) return
    let cancelled = false
    void gateway.permissionStatus().then((status) => {
      if (cancelled || status !== 'granted') return
      void locationStore.dispatch({ kind: 'Start' })
    })
    return () => {
      cancelled = true
    }
  }, [locationStore, gateway])

  const geolocationValue = useMemo(
    () => ({ onMyLocation, dotTarget, isResolvingMyLocation }),
    [onMyLocation, dotTarget, isResolvingMyLocation],
  )

  // TASK-391 §3.2: launch-time geolocation. Runs once when the schema +
  // Location store are ready. Mounted inside AppShell AFTER the URL /
  // localStorage route hydration components, so the geolocation snap
  // deterministically overrides any persisted departure station.
  //
  // Skipped entirely when the URL pins an explicit `?x=&y=&z=` view
  // override — that override is the deterministic deep-link / visual-
  // regression seam and, like cover-fit, must NOT be panned away by the
  // launch camera-focus. The launch geolocation only seeds the *default*
  // initial centre; an explicit pinned view always wins. Likewise a URL that
  // names the route's departure (`?from=`, `/routes/<from>-to-<to>`) keeps it.
  const launchRanRef = useRef(false)
  useEffect(() => {
    if (launchRanRef.current) return
    if (schema === null || locationStore === null) return
    launchRanRef.current = true
    if (readInitialViewOverride() !== null) return
    // A link that names its departure keeps it (and the route frames the
    // camera) — the snap is for an unpinned start.
    if (urlNamesRouteDeparture()) return
    // `location_initial_user_location_requested` — the app-start
    // auto-locate path (mirrors iOS `.requestInitialUserLocation`).
    reportInitialUserLocationRequested()
    // A line view link (`/<locale>/lines/<slug>`) frames the whole line —
    // the launch snap still seeds the departure, but leaves the camera.
    const linePinsCamera = /\/lines\/[^/]+\/?$/.test(window.location.pathname)
    void runLaunchGeolocation({
      // §3.2: read permission WITHOUT prompting — go straight to the
      // gateway's `permissionStatus()` rather than dispatching the
      // prompt-triggering `RequestPermission` event. The first known
      // status at launch is reported as `location_authorization_changed`
      // (mirrors iOS surfacing the initial `.didChangeAuthorization`).
      getPermission: async () => {
        const status = await gateway.permissionStatus()
        reportAuthorizationChanged(status)
        return status
      },
      resolveNearestSnapshot,
      // Launch recenter snaps the camera (no spring) — it establishes the
      // initial view rather than animating from a gesture.
      focusOn: (target) => {
        if (!linePinsCamera) mapFocusHandle.focus(target, { instant: true })
      },
      focusOnStation: (slug) => {
        const resolved = resolveStationBySlug(schema, slug)
        if (resolved === null || linePinsCamera) return
        mapFocusHandle.focus(
          { canvasX: resolved.station.x, canvasY: resolved.station.y },
          { instant: true },
        )
      },
      dispatchMap,
    })
  }, [schema, locationStore, gateway, resolveNearestSnapshot, dispatchMap, mapFocusHandle])

  return (
    <MapFocusContext.Provider value={mapFocusHandle}>
      <GeolocationContext.Provider value={geolocationValue}>
        {children}
        <DeniedModalSlot open={deniedModalOpen} onClose={() => setDeniedModalOpen(false)} />
      </GeolocationContext.Provider>
    </MapFocusContext.Provider>
  )
}

/**
 * Renders the §3.4 location-denied modal when `open`. The "Open
 * Settings" action is best-effort — the modal itself surfaces the
 * per-browser instructions (see {@link LocationDeniedModal}).
 */
function DeniedModalSlot({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}): ReactElement | null {
  if (!open) return null
  return <LocationDeniedModal onClose={onClose} onOpenSettings={() => {}} />
}
